import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Code, Security, Rocket, IntegrationInstructions } from '@mui/icons-material';

const HeroSection = () => {
    const navigate = useNavigate();
    const [query, setQuery] = React.useState('');

    const onSearch = (e) => {
        e.preventDefault();
        const q = query.trim();
        if (!q) return;
        navigate(`/documentation?q=${encodeURIComponent(q)}`);
    };

    return (
        <section className="pt-20 pb-32 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-16 items-center">
                    {/* Left Content */}
                    <div className="space-y-8">
                        <div>
                            <div className="inline-flex items-center px-4 py-2 rounded-full bg-amber-100 border border-amber-200 mb-6">
                                <span className="w-2 h-2 bg-amber-500 rounded-full mr-2 animate-pulse"></span>
                                <span className="text-amber-800 font-medium text-sm">API Disponible • Version 2.1</span>
                            </div>

                            <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight dark:text-slate-100">
                                <span className="block">Intégration API</span>
                                <span className="block bg-gradient-to-r from-amber-500 to-yellow-600 bg-clip-text text-transparent">
                                    Netikash
                                </span>
                            </h1>
                            <p className="text-xl text-gray-700 mt-6 leading-relaxed dark:text-slate-300">
                                Acceptez les paiements en ligne en toute sécurité et sans complexité.
                                Notre plateforme développeur vous offre tous les outils pour intégrer
                                facilement nos services dans vos applications.
                            </p>
                        </div>

                        {/* Features List */}
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                                <span className="text-gray-700 font-medium">SDK Multi-langages</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                                <span className="text-gray-700 font-medium">Documentation complète</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                                <span className="text-gray-700 font-medium">Support technique 24/7</span>
                            </div>
                        </div>

                        {/* Call to Action */}
                        <div className="space-y-6">
                            <div className="flex flex-col sm:flex-row gap-4">
                                <Link
                                    to="/documentation"
                                    className="bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:from-amber-600 hover:to-yellow-700 transition-all transform hover:-translate-y-1 shadow-lg hover:shadow-xl text-center group"
                                >
                                    <span className="flex items-center justify-center">
                                        Explorer la Documentation
                                        <svg className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                    </span>
                                </Link>
                                <Link
                                    to="/examples"
                                    className="border-2 border-amber-300 text-amber-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-amber-500 hover:bg-amber-50 transition-all text-center group"
                                >
                                    <span className="flex items-center justify-center">
                                        Voir les Exemples
                                        <svg className="w-5 h-5 ml-2 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" />
                                        </svg>
                                    </span>
                                </Link>
                            </div>

                            {/* Search + Getting started */}
                            <div className="bg-white/70 backdrop-blur-md rounded-2xl p-6 border border-amber-200/60 shadow-sm">
                                <form onSubmit={onSearch} className="flex flex-col sm:flex-row gap-3">
                                    <input
                                        value={query}
                                        onChange={(e) => setQuery(e.target.value)}
                                        placeholder="Rechercher un endpoint, un mot-clé, un code d'erreur…"
                                        className="flex-1 px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                                    />
                                    <button
                                        type="submit"
                                        className="px-6 py-3 rounded-lg bg-gray-900 text-white font-semibold hover:bg-gray-800 transition-colors"
                                    >
                                        Rechercher
                                    </button>
                                </form>

                                <div className="mt-4 flex flex-wrap gap-3 text-sm">
                                    <span className="text-gray-600 font-semibold mr-1">Premiers pas :</span>
                                    <Link to="/authentication" className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors">
                                        Token OAuth2
                                    </Link>
                                    <Link to="/payment-flow" className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors">
                                        Flux de paiement
                                    </Link>
                                    <Link to="/documentation?q=webhook" className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors">
                                        Webhooks
                                    </Link>
                                    <Link to="/error-codes" className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 hover:bg-amber-200 transition-colors">
                                        Codes d'erreur
                                    </Link>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Content - Graphic */}
                    <div className="relative">
                        <div className="bg-gradient-to-br from-amber-500/10 to-yellow-500/10 rounded-2xl p-8 backdrop-blur-lg border border-amber-200/30 shadow-xl">
                            <div className="grid grid-cols-2 gap-6">
                                {/* API Card */}
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-amber-100 hover:transform hover:-translate-y-2 transition-all duration-300 group">
                                    <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
                                        <Code className="text-amber-600 text-2xl" />
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">API RESTful</h3>
                                    <p className="text-gray-600 text-sm">Endpoints modernes et sécurisés</p>
                                </div>

                                {/* SecurityPage Card */}
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-amber-100 hover:transform hover:-translate-y-2 transition-all duration-300 group">
                                    <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
                                        <Security className="text-amber-600 text-2xl" />
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Sécurité</h3>
                                    <p className="text-gray-600 text-sm">Certifié PCI DSS niveau 1</p>
                                </div>

                                {/* Integration Card */}
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-amber-100 hover:transform hover:-translate-y-2 transition-all duration-300 group">
                                    <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
                                        <IntegrationInstructions className="text-amber-600 text-2xl" />
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Intégration</h3>
                                    <p className="text-gray-600 text-sm">Guides pas à pas</p>
                                </div>

                                {/* Performance Card */}
                                <div className="bg-white rounded-xl p-6 shadow-lg border border-amber-100 hover:transform hover:-translate-y-2 transition-all duration-300 group">
                                    <div className="bg-amber-100 w-12 h-12 rounded-lg flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-colors">
                                        <Rocket className="text-amber-600 text-2xl" />
                                    </div>
                                    <h3 className="font-semibold text-gray-900 mb-2">Performance</h3>
                                    <p className="text-gray-600 text-sm">99.9% uptime garanti</p>
                                </div>
                            </div>

                            {/* Floating Elements */}
                            <div className="absolute -top-4 -right-4 w-24 h-24 bg-amber-200/40 rounded-full blur-xl animate-float"></div>
                            <div className="absolute -bottom-4 -left-4 w-32 h-32 bg-yellow-200/40 rounded-full blur-xl animate-float animation-delay-2000"></div>
                            <div className="absolute top-1/2 -right-8 w-16 h-16 bg-orange-200/30 rounded-full blur-lg animate-float animation-delay-4000"></div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default HeroSection;